import { jwtDecode } from 'jwt-decode';
import { useEffect, useMemo, useState } from 'react';

import { getUser } from './Auth/RequireAuth';
import { useUnidadeDAF } from '../../services/enum/useUnidadeDaf';
import { getUserData } from '../../services/userService';

type JwtVinculo = {
  papel?: string | null;
  status?: string | null;
  interface?: number | null;
  idInstituicao?: number;
  nomeInstituicao?: string | null;
  siglaInstituicao?: string | null;
};

type JwtPayload = {
  sub?: number;
  name?: string;
  email?: string;
  unidade?: any;
  possuiVinculo?: boolean;
  vinculo?: JwtVinculo[];
};

const normalizeRole = (v?: string | null) =>
  String(v ?? '')
    .trim()
    .toUpperCase();

/** #Papel ativo encontrado em vinculo[]  */
export const getRolesFromTokenVinculo = (payload: any): string[] => {
  const p: JwtPayload = payload ?? {};
  const vinculos = Array.isArray(p.vinculo) ? p.vinculo : [];

  const roles = vinculos
    .map((v) => normalizeRole(v?.papel))
    .filter((r) => r.length > 0);

  // remove duplicados mantendo ordem
  return Array.from(new Set(roles));
};

/** #Retorna UM papel (string) ou "" (nenhum) */
export const getPrimaryRoleFromTokenVinculo = (payload: any): string => {
  const roles = getRolesFromTokenVinculo(payload);
  if (!roles.length) return '';

  const priority = ['ADMIN', 'ADMINISTRADOR'];
  const hit = roles.find((r) => priority.includes(r));
  return hit ?? roles[0];
};

/** #possui vínculo se payload.possuiVinculo=true OU payload.vinculo tem itens */
export const hasVinculoFromPayload = (payload: any): boolean => {
  const p: JwtPayload = payload ?? {};
  if (p.possuiVinculo === true) return true;
  if (Array.isArray(p.vinculo) && p.vinculo.length > 0) return true;
  return false;
};

/** #Extrai o nome da instituição do vínculo ativo */
export const getNomeInstituicaoFromVinculo = (payload: any): string | null => {
  const p: JwtPayload = payload ?? {};
  const vinculos = Array.isArray(p.vinculo) ? p.vinculo : [];

  // Busca o primeiro vínculo ativo
  const vinculoAtivo = vinculos.find(
    (v) => v?.status?.toUpperCase() === 'ATIVO',
  );

  return vinculoAtivo?.nomeInstituicao || null;
};

export const getSiglaInstituicaoFromVinculo = (payload: any): string | null => {
  const p: JwtPayload = payload ?? {};
  const vinculos = Array.isArray(p.vinculo) ? p.vinculo : [];

  // Busca o primeiro vínculo ativo
  const vinculoAtivo = vinculos.find(
    (v) => v?.status?.toUpperCase() === 'ATIVO',
  );

  return vinculoAtivo?.siglaInstituicao || null;
};

/**  #se o papel principal for ADMIN/ADMINISTRADOR (ou ajuste para outros) */
export const isAdmin = (papel: string | null | undefined): boolean => {
  const r = normalizeRole(papel);
  return r === 'ADMIN' || r === 'ADMINISTRADOR';
};

/** #Hook principal que você usa no AppDAF / Sidebar */
export function useNomeCompleto() {
  const [nomeCompleto, setNomeCompleto] = useState<string>('Carregando...');
  const [nomeInstituicao, setNomeInstituicao] = useState<string | null>(null);
  const [siglaInstituicao, setSiglaInstituicao] = useState<string | null>(null);
  const [papel, setPapel] = useState<string>('');
  const [possuiVinculo, setPossuiVinculo] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const unidadeDAF = useUnidadeDAF();

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      try {
        const token = localStorage.getItem('token');
        const tokenPayload: any = token ? jwtDecode(token) : null;

        if (tokenPayload && tokenPayload.sub) {
          // #vinculo e papel vêm do TOKEN
          const hasV = hasVinculoFromPayload(tokenPayload);
          const role = getPrimaryRoleFromTokenVinculo(tokenPayload);

          // Extrai o nome da instituição do token primeiro
          const instituicaoFromToken =
            getNomeInstituicaoFromVinculo(tokenPayload);

          const siglaInstituicaoFromToken =
            getSiglaInstituicaoFromVinculo(tokenPayload);

          // #nome/unidade podem vir do backend (userData)
          const userData = await getUserData(tokenPayload.sub);

          if (!isMounted) return;

          setNomeCompleto(
            userData?.nomeCompleto || tokenPayload?.name || 'Usuário',
          );

          setNomeInstituicao(
            userData?.nomeInstituicao || instituicaoFromToken || null,
          );
          setSiglaInstituicao(
            userData?.siglaInstituicao || siglaInstituicaoFromToken || null,
          );
          setPapel(role);
          setPossuiVinculo(hasV);
        } else {
          if (!isMounted) return;
          setNomeCompleto('Usuário');
          setNomeInstituicao(null);
          setSiglaInstituicao(null);
          setPapel('');
          setPossuiVinculo(false);
        }
      } catch (error) {
        if (!isMounted) return;
        setNomeCompleto('Usuário');
        setNomeInstituicao(null);
        setSiglaInstituicao(null);
        setPapel('');
        setPossuiVinculo(false);
      } finally {
        if (!isMounted) return;
        setIsLoading(false);
      }
    };

    fetchData();
    return () => {
      isMounted = false;
    };
  }, [unidadeDAF]);

  return {
    nomeCompleto,
    siglaInstituicao,
    papel,
    isLoading,
    nomeInstituicao,
    possuiVinculo,
  };
}

export const hasTpVinculo = (): boolean => {
  try {
    const token = localStorage.getItem('token');
    if (!token) return false;
    const decoded: any = jwtDecode(token);
    return decoded.tpVinculo === '1' || decoded.tpVinculo === 1;
  } catch (error) {
    console.error('Erro ao decodificar o token JWT:', error);
    return false;
  }
};
